import { Trailer, ITrailer } from "../../../model/Trailer";
import { getServerDate } from "../../../utils/storage/date";
import Api from "../../../services/api";
import { TMultiCallParam } from "../../../utils/api.interfaces";
import { TrailerAttachment } from "../../../model/TrailerAttachment";
import idSerializer from "geotab-uuid";
import constants from "../../../constants";
import { Device } from "../../../model/Device";
import { getTrailerGroups, addAsset, getAsset } from "../../../sync/deviceApiHelpers";
import Sync from "../../../sync/sync";
import { UserService } from "../../../utils/users";
import { BaseCache } from "../../../utils/storage/cache";
import { usesTrailerDeviceIdentity } from "../../../sync/trailerAttachmentDeviceLink";

const MAX_SEARCH_ASSETS = 20;

/**
 * Removes duplicate entities by ID
 */
export function makeUnique<T extends INamedEntity>(entities: T[]): T[] {
    let unique = {};
    let result: T[] = [];

    entities.forEach(entity => {
        if (!unique[entity.id]) {
            unique[entity.id] = true;
            result.push(entity);
        }
    });

    return result;
}

/**
 * Trims whitespace and limits trailer name to 255 characters
 */
export function cleanTrailerName(trailerName: string): string {
    return trailerName.trim().substring(0, 255);
}

/**
 * Case-insensitive check if a trailer with the same name exists in the list
 */
export function hasTrailerWithSameName(trailerName: string, trailers: ITrailer[]): boolean {
    for (let i = 0; i < trailers.length; i++) {
        let trailer = trailers[i];
        if (trailer.name.toLowerCase() === trailerName.toLowerCase()) {
            return true;
        }
    }
    return false;
}

/**
 * Converts IDevice to Trailer.
 *
 * MYG-148762: with DRIVE.ENABLE_TRAILER_DEVICE_LINK_API the trailer is identified by its own
 * Device.id; tmpTrailerId is only consulted on the legacy TrailerAttachment path, where it can
 * legitimately be empty and would otherwise produce an attachment with no trailer id.
 */
export function mapDeviceToTrailer(device: IDevice): ITrailer {
    return new Trailer({
        ...device,
        // No store in hand here (mapDeviceToTrailer is called from plain helpers), so this is the one
        // place that falls back to the global inside usesTrailerDeviceIdentity.
        id: usesTrailerDeviceIdentity() ? device.id : device.tmpTrailerId
    });
}

/**
 * Executes multiCall API request, sorts results (exact match first, then alphabetical), and deduplicates.
 *
 * The API's "strict" name search (no wildcards) does a prefix match, not exact match, so strict results
 * may include names beyond the search term. We sort everything together alphabetically and elevate only
 * the single true exact match to the top.
 */
export function runMultiCallSearch<T extends INamedEntity>(
    api: Api,
    calls: TMultiCallParam[],
    exactSearchTerm?: string
): Promise<T[]> {
    return new Promise<T[]>((resolve, reject) => {
        api.multiCall(calls, (results: T[][]) => {
            let assets: T[] = [];
            if (results.length) {
                const strictResults = calls.length > 1 ? (results[1] || []) : [];
                const wildcardResults = results[0] || [];
                // Merge strict and wildcard results, deduplicating by id
                const merged = makeUnique([...strictResults, ...wildcardResults]);
                const lowerTerm = exactSearchTerm ? exactSearchTerm.toLowerCase() : null;
                const exactMatch = lowerTerm ? merged.find(r => r.name.toLowerCase() === lowerTerm) : null;
                const rest = merged.filter(r => r !== exactMatch);
                rest.sort((a, b) => a.name.localeCompare(b.name));
                assets = exactMatch ? [exactMatch, ...rest] : rest;
            }
            resolve(assets);
        }, (errorObject) => {
            reject(errorObject);
        });
    });
}

/**
 * Searches for trailers using dual API search (wildcard + exact match)
 * @param api - API instance
 * @param searchInput - Search term (trailer name, VIN, or serial number)
 * @param maxResults - Maximum number of results to return (default: 20)
 * @returns Promise resolving to array of trailers
 */
export function searchTrailers(
    api: Api,
    searchInput: string,
    maxResults: number = MAX_SEARCH_ASSETS
): Promise<ITrailer[]> {
    const fromDate = getServerDate().toISOString();
    const calls: TMultiCallParam[] = [
        [
            "Get", {
                typeName: "Device",
                search: {
                    ...(searchInput && { name: `%${ searchInput }%` }),
                    groups: [{
                        id: "GroupTrailerId"
                    }],
                    fromDate
                },
                resultsLimit: maxResults
            }
        ]
    ];

    if (searchInput) {
        calls.push([
            "Get", {
                typeName: "Device",
                search: {
                    name: searchInput,
                    groups: [{
                        id: "GroupTrailerId"
                    }],
                    fromDate
                },
                resultsLimit: 5
            }
        ]);
    }

    return runMultiCallSearch<IDevice>(api, calls, searchInput).then((devices: IDevice[]) => {
        return devices.map(device => mapDeviceToTrailer(device));
    });
}

/**
 * Creates a new trailer Device object
 * @param trailerName - Name for the new trailer
 * @param trailerComment - Optional comment/description for the trailer
 * @param timezoneId - Timezone ID for the trailer (typically from user)
 * @returns New Device configured as a trailer
 */
export function createNewTrailerDevice(trailerName: string, trailerComment: string, timezoneId?: string): Device {
    return new Device({
        id: "",
        name: trailerName,
        comment: trailerComment,
        activeFrom: getServerDate().toISOString(),
        activeTo: new Date(Date.UTC(2050, 0, 1)).toISOString(),
        serialNumber: "",
        timezoneId: timezoneId
    });
}

/**
 * Creates an offline trailer (for use when network is unavailable)
 * @param trailerName - Name for the new trailer
 * @param trailerComment - Optional comment/description for the trailer
 * @param timezoneId - Timezone ID for the trailer (typically from user)
 * @returns Trailer object marked as temporary
 */
export function createOfflineTrailer(trailerName: string, trailerComment: string, timezoneId?: string): Trailer {
    const trailer = new Trailer({
        id: idSerializer.get(),
        name: trailerName,
        comment: trailerComment,
        activeFrom: getServerDate().toISOString(),
        activeTo: new Date(Date.UTC(2050, 0, 1)).toISOString(),
        serialNumber: "",
        timezoneId,
        _isTemp: true
    });
    return trailer;
}

/**
 * Creates and caches an offline trailer when network is unavailable
 * Adds the trailer to the cache for the current user and all co-drivers
 *
 * @param trailerName - Name for the new trailer
 * @param trailerComment - Optional comment/description
 * @param userService - User service for getting current user and co-drivers
 * @param trailerCache - Trailer cache instance
 * @returns Created offline Trailer
 */
export function createAndCacheOfflineTrailer(
    trailerName: string,
    trailerComment: string,
    userService: UserService,
    trailerCache: BaseCache<ITrailer>
): Trailer {
    const user = userService.getCurrentUser();
    const offlineTrailer = createOfflineTrailer(trailerName, trailerComment, user?.timeZoneId);

    // Add the offline trailer to the cache
    trailerCache.add(offlineTrailer);

    // Also add to co-drivers if any
    const codrivers = userService.getCoDrivers(user?.name);
    if (codrivers && codrivers.length > 0) {
        for (const codriver of codrivers) {
            trailerCache.add(offlineTrailer, codriver.id);
        }
    }

    return offlineTrailer;
}

/**
 * Detaches all trailer attachments
 * Only syncs non-temporary attachments (temp attachments were created offline and never saved to server)
 *
 * @param attachments - Array of trailer attachments to detach
 * @param sync - Sync service instance
 * @returns Promise that resolves when all detachments are complete
 */
export function detachAllTrailers(attachments: any[], sync: Sync): Promise<void[]> {
    return Promise.all(attachments.map(attachment => {
        attachment.detach();
        // Only sync non-temporary attachments
        if (!attachment._isTemp) {
            return sync.trailerAttachments.setItem(attachment, true);
        }
        return Promise.resolve();
    }));
}

/**
 * Adds a new trailer to the system
 * @param api - API instance
 * @param trailerName - Name for the new trailer
 * @param trailerComment - Optional comment/description
 * @param userCompanyGroups - User's company groups for determining trailer groups
 * @param timezoneId - Timezone ID (typically from user)
 * @returns Promise resolving to the created Trailer, keyed by mapDeviceToTrailer for the active identity
 */
export function addNewTrailer(
    api: Api,
    trailerName: string,
    trailerComment: string,
    userCompanyGroups: DeepReadonlyArray<IGroup> = [],
    timezoneId?: string
): Promise<ITrailer> {
    const newTrailer = createNewTrailerDevice(trailerName, trailerComment, timezoneId);

    return getTrailerGroups(api, userCompanyGroups).then(groups => {
        newTrailer.groups = groups;
        return addAsset(api, newTrailer, "Device");
    }).then((assetId: string) => {
        // Re-read the created asset so mapDeviceToTrailer can key it: its own Device.id under
        // DRIVE.ENABLE_TRAILER_DEVICE_LINK_API.RELEASE, tmpTrailerId on the legacy path.
        return getAsset(api, { id: assetId }, "Device");
    }).then((results: IDevice[]) => {
        if (results && results.length) {
            return mapDeviceToTrailer(results[0]);
        } else {
            throw new Error("Device not found in the database");
        }
    });
}

/**
 * Creates a new TrailerAttachment instance for attaching a trailer to a vehicle
 * @param vehicleId - ID of the vehicle to attach the trailer to
 * @param trailerId - ID of the trailer to attach
 * @returns TrailerAttachment instance ready to be synced
 */
export function createTrailerAttachment(vehicleId: string, trailerId: string): TrailerAttachment {
    return new TrailerAttachment({
        id: idSerializer.get(),
        device: { id: vehicleId },
        trailer: { id: trailerId },
        activeFrom: getServerDate().toISOString(),
        activeTo: constants.MAX_DATE.toISOString()
    });
}

/**
 * Sorts trailers with attached ones first, then unattached.
 * Used in Zenith MobileSheet implementation to display attached trailers at the top.
 *
 * @param trailers - Array of trailers to sort
 * @param attachedTrailerIds - Array of IDs for currently attached trailers
 * @returns Object containing sorted trailers and whether a divider should be shown
 */
export function sortTrailersByAttachmentStatus(
    trailers: ITrailer[],
    attachedTrailerIds: string[]
): { sortedTrailers: ITrailer[]; attachedTrailers: ITrailer[]; unattachedTrailers: ITrailer[]; showDivider: boolean } {
    const attachedTrailers = trailers.filter(t => attachedTrailerIds.indexOf(t.id) !== -1);
    const unattachedTrailers = trailers.filter(t => attachedTrailerIds.indexOf(t.id) === -1);
    // Attached first, then unattached
    const sortedTrailers = attachedTrailers.concat(unattachedTrailers);
    const showDivider = attachedTrailers.length > 0 && unattachedTrailers.length > 0;

    return {
        sortedTrailers,
        attachedTrailers,
        unattachedTrailers,
        showDivider
    };
}
